/**
 * Filter Drawer Manager
 * Handles the rendering and state management of filters within the drawer
 */
class FilterDrawerManager {
    constructor() {
        this.data = window.complexFilterDataDrawer || {
            simpleFilters: [],
            importantFilters: '',
            swatchFilters: '',
            swatchExtension: '.png'
        };
        this.container = document.getElementById('filter_items_container');
        this.allFiltersToRender = [];
        this.swatchFilters = this.parseCSV(this.data.swatchFilters);
        this.swatchExtension = this.data.swatchExtension;
        this.importantFilters = this.parseCSV(this.data.importantFilters);
    }

    parseCSV(str) {
        if (!str || str === '') return [];
        return str.split(',').map(f => f.trim().toLowerCase()).filter(f => f !== '');
    }

    init() {
        this.processFilters();
        this.render();
        this.initializeAccordions();
        this.initializeSearch();
        this.syncFromURL();
    }

    syncFromURL() {
        const params = new URLSearchParams(window.location.search);
        const activeFilters = new Map();

        for (const [key, value] of params.entries()) {
            if (key.startsWith('filter.')) {
                if (!activeFilters.has(key)) {
                    activeFilters.set(key, []);
                }
                activeFilters.get(key).push(value);
            }
        }

        const checkboxes = this.container.querySelectorAll('.filter-checkbox');

        checkboxes.forEach(checkbox => {
            const paramName = checkbox.getAttribute('data-param-name');
            const paramValue = checkbox.getAttribute('data-param-value');

            if (paramName && paramValue) {
                const activeValues = activeFilters.get(paramName) || [];
                const shouldBeChecked = activeValues.includes(paramValue);

                checkbox.checked = shouldBeChecked;

                const label = checkbox.closest('.filter-option');
                const countElement = label?.querySelector('.filter-count');

                if (countElement) {
                    const countText = countElement.textContent.trim();
                    const match = countText.match(/\((\d+)\)/);
                    const count = match ? parseInt(match[1]) : 0;

                    const shouldBeDisabled = count === 0 && !shouldBeChecked;

                    checkbox.disabled = shouldBeDisabled;

                    if (shouldBeDisabled) {
                        label.classList.add('disabled');
                    } else {
                        label.classList.remove('disabled');
                    }
                }
            }
        });

        this.updateActiveFilterCounts();
    }

    updateActiveFilterCounts() {
        const filterGroups = this.container.querySelectorAll('.filter-group');

        filterGroups.forEach(group => {
            const summary = group.querySelector('.filter-summary');
            const labelSpan = summary?.querySelector('.filter-label');
            const checkedBoxes = group.querySelectorAll('.filter-checkbox:checked');
            const count = checkedBoxes.length;

            const oldCount = labelSpan?.querySelector('.filter-active-count');
            if (oldCount) oldCount.remove();

            if (count > 0 && labelSpan) {
                const countSpan = document.createElement('span');
                countSpan.className = 'filter-active-count';
                countSpan.textContent = ` (${count})`;
                countSpan.style.color = '#666';
                countSpan.style.fontWeight = '400';
                labelSpan.appendChild(countSpan);
            }
        });
    }

    processFilters() {
        this.data.simpleFilters.forEach(filter => {
            const downcase = filter.label.toLowerCase();
            if (downcase === 'color/finish') return;

            const isComplexSubfilter = filter.type === 'complex';
            const activeCount = 0;
            const isImportant = this.importantFilters.includes(downcase);
            const hasSwatches = this.swatchFilters.includes(downcase);

            this.allFiltersToRender.push({
                type: isComplexSubfilter ? 'complex' : 'simple',
                label: filter.label,
                labelKey: filter.labelKey,
                parentLabel: filter.parentLabel || null,
                parentParamName: filter.parentParamName || null,
                paramName: filter.paramName,
                filterType: filter.filterType,
                operator: filter.operator,
                values: filter.values,
                activeCount: activeCount,
                isImportant: isImportant,
                hasSwatches: hasSwatches,
                isExpanded: isImportant,
                order: filter.order !== undefined ? filter.order : 999
            });
        });

        this.allFiltersToRender.sort((a, b) => a.order - b.order);
    }

    render() {
        if (!this.container) return;

        this.container.innerHTML = '';

        this.allFiltersToRender.forEach((filter, index) => {
            const accordion = this.createFilterAccordion(filter, index);
            this.container.appendChild(accordion);
        });
    }

    createFilterAccordion(filter, index) {
        const wrapper = document.createElement('div');
        wrapper.className = 'filter-group';
        wrapper.setAttribute('data-filter-id', this.handleize(filter.label));
        wrapper.setAttribute('data-filter-group', `drawer-filter-${index}`);

        if (filter.type === 'complex') {
            wrapper.setAttribute('data-complex-parent', filter.parentLabel);
            wrapper.setAttribute('data-complex-group', filter.label);
        }

        const details = document.createElement('details');
        details.className = 'filter-details';
        if (filter.isExpanded) {
            details.setAttribute('open', '');
        }

        const summary = document.createElement('summary');
        summary.className = 'filter-summary';

        const labelSpan = document.createElement('span');
        labelSpan.className = 'filter-label';
        labelSpan.textContent = filter.label;

        summary.appendChild(labelSpan);

        const icon = document.createElement('span');
        icon.className = 'filter-icon';
        icon.innerHTML = '<i class="ph ph-plus"></i>';
        summary.appendChild(icon);

        details.appendChild(summary);

        const optionsDiv = document.createElement('div');
        optionsDiv.className = 'filter-options';
        optionsDiv.addEventListener('click', (e) => e.stopPropagation());

        if (filter.values.length > 10) {
            const searchWrapper = document.createElement('div');
            searchWrapper.className = 'filter-drawer__search';

            const searchInput = document.createElement('input');
            searchInput.type = 'text';
            searchInput.className = 'filter-drawer__search-input';
            searchInput.placeholder = `Search ${filter.label.toLowerCase()}...`;
            searchInput.setAttribute('data-search-for', `drawer-filter-${index}`);

            searchWrapper.appendChild(searchInput);
            optionsDiv.appendChild(searchWrapper);
        }

        const sortedValues = [...filter.values].sort((a, b) => {
            if (a.active !== b.active) return a.active ? -1 : 1;
            if (a.count !== b.count) return b.count - a.count;
            return a.label.localeCompare(b.label);
        });

        sortedValues.forEach((value, valueIndex) => {
            const option = this.createFilterOption(filter, value, index, valueIndex);
            optionsDiv.appendChild(option);
        });

        details.appendChild(optionsDiv);
        wrapper.appendChild(details);

        return wrapper;
    }

    createFilterOption(filter, value, filterIndex, valueIndex) {
        const label = document.createElement('label');
        label.className = 'filter-option';
        label.setAttribute('data-filter-value', value.label.toLowerCase());

        if (filter.hasSwatches) {
            label.classList.add('filter-option--has-swatch');
        }

        const isDisabled = value.count === 0 && !value.active;
        if (isDisabled) {
            label.classList.add('disabled');
        }

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'filter-checkbox';
        checkbox.id = `drawer-filter-${filterIndex}-${valueIndex}`;

        if (filter.type === 'complex') {
            checkbox.name = value.paramName;
            checkbox.value = value.paramValue;
        } else {
            checkbox.name = value.paramName;
            checkbox.value = value.value;
        }

        checkbox.setAttribute('data-param-name', checkbox.name);
        checkbox.setAttribute('data-param-value', checkbox.value);

        if (isDisabled) {
            checkbox.disabled = true;
        }

        checkbox.addEventListener('change', () => {
            this.updateActiveFilterCounts();
            this.updateApplyButtonCount();
        });

        label.appendChild(checkbox);

        const contentSpan = document.createElement('span');
        contentSpan.className = 'filter-option-content';

        if (filter.hasSwatches) {
            const swatchName = this.getSwatchName(value.label);
            const swatchPath = `swatch-${swatchName}${this.swatchExtension}`;

            const img = document.createElement('img');
            img.src = swatchPath;
            img.alt = value.label;
            img.className = 'filter-swatch';
            img.loading = 'lazy';
            img.onerror = function() {
                this.classList.add('filter-swatch--error');
                this.style.display = 'none';
            };

            contentSpan.appendChild(img);
        }

        const optionLabel = document.createElement('span');
        optionLabel.className = 'filter-option-label';
        optionLabel.textContent = value.label + ' ';

        const countSpan = document.createElement('span');
        countSpan.className = 'filter-count';
        countSpan.textContent = `(${value.count})`;
        optionLabel.appendChild(countSpan);

        contentSpan.appendChild(optionLabel);

        const checkmark = document.createElement('span');
        checkmark.className = 'filter-checkmark';
        checkmark.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M13.3346 4L6.0013 11.3333L2.66797 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        `;
        contentSpan.appendChild(checkmark);

        label.appendChild(contentSpan);

        return label;
    }

    updateApplyButtonCount() {
        const checkedCount = this.container.querySelectorAll('.filter-checkbox:checked').length;
        const applyButton = document.querySelector('#apply-filters');

        if (applyButton) {
            applyButton.textContent = checkedCount > 0
                ? `Apply Filters (${checkedCount})`
                : 'Apply Filters';
        }
    }

    getSwatchName(label) {
        const parts = label.split('=');
        const valueToUse = parts.length > 1 ? parts[parts.length - 1].trim() : label;
        return this.handleize(valueToUse);
    }

    handleize(str) {
        return str
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    initializeAccordions() {
        const details = this.container.querySelectorAll('.filter-details');

        details.forEach(detail => {
            const summary = detail.querySelector('.filter-summary');
            const icon = summary?.querySelector('.filter-icon i');

            if (!icon) return;

            icon.className = detail.open ? 'ph ph-minus' : 'ph ph-plus';

            detail.addEventListener('toggle', () => {
                icon.className = detail.open ? 'ph ph-minus' : 'ph ph-plus';
            });
        });
    }

    initializeSearch() {
        const searchInputs = this.container.querySelectorAll('.filter-drawer__search-input');

        searchInputs.forEach(input => {
            const filterGroup = input.getAttribute('data-search-for');
            const groupEl = document.querySelector(`[data-filter-group="${filterGroup}"]`);

            if (!groupEl) return;

            const options = groupEl.querySelectorAll('.filter-option');

            input.addEventListener('input', (e) => {
                const searchTerm = e.target.value.toLowerCase();

                options.forEach(option => {
                    const filterValue = option.getAttribute('data-filter-value') || '';
                    option.style.display = filterValue.includes(searchTerm) ? '' : 'none';
                });
            });
        });
    }
}

/**
 * Filter Drawer Component
 * Extends DrawerComponent to provide a slide-out filter panel
 */
class FilterDrawer extends DrawerComponent {
    constructor() {
        super();
        this.sectionId = this.getAttribute('data-section-id');
        this.filterManager = null;
        this.isRefreshing = false;
        this.skipUntilTimestamp = 0;
        this.boundFacetsUpdatedHandler = null;

        this.boundFooterClickHandler = this.handleFooterClick.bind(this);
        this.boundCheckboxChangeHandler = this.handleCheckboxChange.bind(this);
    }

    get requiresBodyAppended() {
        return false;
    }

    connectedCallback() {
        super.connectedCallback();
        this.initializeFilterDrawer();

    }

    disconnectedCallback() {
        const footer = this.querySelector('.filter-drawer__footer');
        if (footer) {
            footer.removeEventListener('click', this.boundFooterClickHandler);
        }

        this.removeEventListener('change', this.boundCheckboxChangeHandler);
    }

    initializeFilterDrawer() {
        this.filterManager = new FilterDrawerManager();
        this.filterManager.init();
        this.setupButtons();
    }

    setupButtons() {
        const footer = this.querySelector('.filter-drawer__footer');
        if (footer) {
            footer.removeEventListener('click', this.boundFooterClickHandler);
            footer.addEventListener('click', this.boundFooterClickHandler);
        }

        this.removeEventListener('change', this.boundCheckboxChangeHandler);
        this.addEventListener('change', this.boundCheckboxChangeHandler);
    }

    handleFooterClick(e) {
        const applyButton = e.target.closest('#apply-filters');
        const clearButton = e.target.closest('#clear-all-filters');

        if (applyButton) {
            e.preventDefault();
            e.stopPropagation();
            this.handleApplyFilters();
            return;
        }

        if (clearButton) {
            e.preventDefault();
            e.stopPropagation();
            this.handleClearFilters();
            return;
        }
    }

    handleCheckboxChange(e) {
        if (e.target.classList.contains('filter-checkbox')) {
            this.updateApplyButtonCount();
        }
    }

    handleApplyFilters() {
        const url = this.buildFilterUrl();

        // Skip all facets:updated events for the next 500ms
        this.skipUntilTimestamp = Date.now() + 500;

        this.hide();

        const facetForm = document.querySelector('form[is="facet-form"]');
        if (facetForm && typeof facetForm.renderSection === 'function') {
            facetForm.renderSection(url, null);
        } else {
            window.location.href = url;
        }
    }

    handleClearFilters() {
        const params = new URLSearchParams(window.location.search);

        for (let key of [...params.keys()]) {
            if (key.startsWith('filter.')) {
                params.delete(key);
            }
        }

        const fullUrl = new URL(window.location.pathname, window.location.origin);
        fullUrl.search = params.toString();
        const url = fullUrl.toString();

        // Skip all facets:updated events for the next 500ms
        this.skipUntilTimestamp = Date.now() + 500;

        this.hide();

        const facetForm = document.querySelector('form[is="facet-form"]');
        if (facetForm && typeof facetForm.renderSection === 'function') {
            facetForm.renderSection(url, null);
        } else {
            window.location.href = url;
        }
    }

    buildFilterUrl() {
        const params = new URLSearchParams(window.location.search);

        for (let key of [...params.keys()]) {
            if (key.startsWith('filter.')) {
                params.delete(key);
            }
        }

        const checkedBoxes = this.querySelectorAll('.filter-checkbox:checked');

        checkedBoxes.forEach(checkbox => {
            const name = checkbox.getAttribute('name');
            const value = checkbox.getAttribute('value');

            if (name && value) {
                params.append(name, value);
            }
        });

        const fullUrl = new URL(window.location.pathname, window.location.origin);
        fullUrl.search = params.toString();

        return fullUrl.toString();
    }

    updateApplyButtonCount() {
        const checkedCount = this.querySelectorAll('.filter-checkbox:checked').length;
        const applyButton = this.querySelector('#apply-filters');

        if (applyButton) {
            applyButton.textContent = checkedCount > 0
                ? `Apply Filters (${checkedCount})`
                : 'Apply Filters';
        }
    }

    async show(focusElement = null, animate = true) {
        super.show(focusElement, animate);

        if (this.open && !Shopify.designMode) {
            FoxTheme?.a11y?.trapFocus(this, this.focusElement || this.querySelector('.drawer__close-btn'));
        }

        await this.refreshDrawerFromServer();
    }

    async refreshDrawerFromServer() {
        if (this.isRefreshing) {
            return;
        }

        this.isRefreshing = true;

        try {
            const url = new URL(window.location.href);
            url.searchParams.set('section_id', this.sectionId);

            const response = await fetch(url.toString());
            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            const scripts = doc.querySelectorAll('script');

            // Reset the global data first
            window.complexFilterDataDrawer = {
                simpleFilters: [],
                importantFilters: '',
                swatchFilters: '',
                swatchExtension: '.png'
            };

            // Execute all scripts that contain filter data
            scripts.forEach(scriptElement => {
                const scriptText = scriptElement.textContent;

                if (scriptText && (
                    scriptText.includes('window.complexFilterDataDrawer') ||
                    scriptText.includes('subFilterData') ||
                    scriptText.includes('simpleFilters.push')
                )) {
                    try {
                        eval(scriptText);
                    } catch (error) {
                        // Script execution failed silently
                    }
                }
            });

            // Rebuild drawer manager with fresh data
            this.filterManager = new FilterDrawerManager();
            this.filterManager.init();

            this.setupButtons();
        } catch (error) {
            // Drawer refresh failed silently
        } finally {
            this.isRefreshing = false;
        }
    }
}

/**
 * Global trigger for opening the filter drawer
 */
document.addEventListener('click', function(e) {
    const trigger = e.target.closest('#filter-selector-trigger');
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    const drawer = document.getElementById('FilterDrawer');
    if (drawer && typeof drawer.show === 'function') {
        drawer.show();
    }
});

/**
 * Register custom element
 */
try {
    customElements.define('filter-drawer', FilterDrawer);
} catch (error) {
    // Element registration failed
}